import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Image from 'components/Common/Image/Image'
import FormatCurrency from '../FormatCurrency'
import ContactContext from 'contexts/contactContext'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { FlightsClassTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import Colour from 'components/utils/Colour'
import { formatPlacePhrase } from 'i18n/formatters/placePhrase/formatPlacePhrase'

interface Props {
  carrierLogo?: string
  carrierName: string
  destinationName: string
  price: number
  onCheckAvailability: () => void
  fareType: App.FlightsFareType
  fareClass: FlightsClassTypes
}

function UpsellBanner(props: Props) {
  const {
    carrierLogo,
    carrierName,
    destinationName,
    price,
    onCheckAvailability,
    fareType,
    fareClass,
  } = props

  const intl = useIntl()
  const { fareTypeLabelMap, fareCabinLabelMap } = useFlightFareLabels()
  const geoContact = useContext(ContactContext)

  return (
    <MessageBanner
      primaryActionButtonProps={{
        children: intl.formatMessage({
          id: 'Udt292NjzxGOuu23wSQD',
          defaultMessage: 'Check availability',
          description: 'Button text to check flight availability in the flights flow',
        }),
        onClick: onCheckAvailability,
      }}
      secondaryActionButtonProps={{
        children: geoContact.defaultContact.humanReadable,
        href: `tel:${geoContact.defaultContact.number}`,
        startIcon: <LinePhoneIcon />,
      }}
      kind="promotional-primary"
    >
      <Group direction="horizontal" verticalAlign="center" gap="0 12">
        {carrierLogo && (
          <CSSBreakpoint min="tablet">
            <Image
              width={100}
              image={{ url: carrierLogo, title: carrierName }}
            />
          </CSSBreakpoint>
        )}

        <BodyText variant="medium" weight="bold">
          <Colour value="urgency">
            <FormattedMessage
              id="CZuEALeQh8umWK_aHye4"
              defaultMessage="<fireIcon></fireIcon> Limited time offer"
              description="Urgency label for flight upsell banner in the flight search or booking flow"
              values={{
                fireIcon: () => <SolidFireIcon />,
              }}
            />
          </Colour><br />
          <FormattedMessage
            id="ELEpsmUh_2_kVYNF5Q_y"
            defaultMessage="{fareType} flights to {destinationName} from {price}*/adult in {fareClass} with {carrierName}."
            description="Flight upsell offer details showing fare type, destination, price per adult, cabin class and carrier name"
            values={{
              fareType: fareTypeLabelMap[fareType],
              destinationName: formatPlacePhrase({ name: destinationName }, intl.locale, 'destination'),
              price: <FormatCurrency value={price} format="roundedDollar" />,
              fareClass: fareCabinLabelMap[fareClass],
              carrierName,
            }}
          />
        </BodyText>
      </Group>
    </MessageBanner>
  )
}

export default UpsellBanner
